import { PaginationResult } from '@shared/pagination';
import React, { ChangeEvent, forwardRef, ReactNode, useId, useState } from 'react';
import { Noop } from 'react-hook-form';

import { LazyLoadSelectList } from '@/components/form-controls';
import { useLookupsService } from '@/hooks/use-lookups-service';
import { Lookup } from '@/types/lookup';

type DrugLookupSelectProps = {
  required?: boolean;
  disabled?: boolean;
  name: string;
  value?: Lookup;
  error?: ReactNode;
  onBlur?: Noop;
  onChange: (nextValue: Lookup | ChangeEvent) => void;
  label?: string;
  clear?: { label: string };
  clientId?: number;
  excludeMarketBasketId?: number;
};

export const DrugLookupSelect = forwardRef<HTMLInputElement, DrugLookupSelectProps>((props, ref) => {
  const {
    required,
    disabled,
    name,
    value,
    error,
    onBlur,
    onChange,
    label,
    clear,
    clientId,
    excludeMarketBasketId
  } = props;

  const id = useId();
  const [isLoading, setIsLoading] = useState(false);

  const { getProductLookups } = useLookupsService();

  return (
    <LazyLoadSelectList
      id={id}
      required={required}
      name={name}
      disabled={disabled || isLoading}
      error={error}
      label={label}
      placeholder={isLoading ? 'Loading products...' : 'Select a product'}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      lazyLoadRequest={async (searchTerm, page, rpp) => {
        setIsLoading(true);
        try {
          const result = await getProductLookups({
            query: searchTerm,
            clientId: clientId,
            excludeMarketBasketId,
            page,
            rpp,
          });
          return result as PaginationResult<Lookup>;
        } finally {
          setIsLoading(false);
        }
      }}
      ref={ref}
      clear={clear}
    />
  );
});
